import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSubStaff, createSubStaff, deleteSubStaff, resetSubStaffPassword, getSubStaffActivity } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { UserCirclePlus, Trash, Users, Key, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MyTeamPage() {
  const { user } = useAuth();
  const isTeamLead = user?.role === 'staff';

  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', mobile: '' });
  const [submitting, setSubmitting] = useState(false);

  // Password reset state
  const [resetDialog, setResetDialog] = useState({ open: false, sub: null });
  const [newPw, setNewPw] = useState('');
  const [resetting, setResetting] = useState(false);

  // Activity log state
  const [activityDialog, setActivityDialog] = useState({ open: false, sub: null });
  const [activityList, setActivityList] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const fetchTeam = useCallback(async () => {
    if (!isTeamLead) return;
    setLoading(true);
    try {
      const { data } = await getSubStaff();
      setTeam(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [isTeamLead]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createSubStaff(form);
      toast.success('Sub-staff created');
      setForm({ name: '', email: '', password: '', mobile: '' });
      setDialogOpen(false);
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`${name} ko delete karna hai?`)) return;
    try {
      await deleteSubStaff(id);
      toast.success('Deleted');
      fetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const openResetDialog = (sub) => {
    setResetDialog({ open: true, sub });
    setNewPw('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error('Password minimum 6 characters ka hona chahiye');
      return;
    }
    setResetting(true);
    try {
      await resetSubStaffPassword(resetDialog.sub.id, newPw);
      toast.success(`${resetDialog.sub.name} ka password reset ho gaya`);
      setResetDialog({ open: false, sub: null });
      setNewPw('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset fail hua');
    } finally {
      setResetting(false);
    }
  };

  const openActivityDialog = async (sub) => {
    setActivityDialog({ open: true, sub });
    setActivityLoading(true);
    setActivityList([]);
    try {
      const { data } = await getSubStaffActivity(sub.id);
      setActivityList(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Activity load fail');
    } finally {
      setActivityLoading(false);
    }
  };

  if (!isTeamLead) {
    return <div className="p-6 text-center text-red-600 font-medium" data-testid="my-team-access-denied">
      Ye page sirf Team Lead (staff) ke liye hai.
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="my-team-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={24} className="text-violet-600" /> My Team
          </h1>
          <p className="text-sm text-gray-500 mt-1">Apne sub-staff add/manage karo — unke login credentials.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-700 self-start" data-testid="add-sub-staff-btn">
              <UserCirclePlus size={18} className="mr-2" /> Add Sub-Staff
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Sub-Staff</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-medium">Name <span className="text-red-500">*</span></Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Sub-staff name" required data-testid="sub-staff-name-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Email <span className="text-red-500">*</span></Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="Login email" required data-testid="sub-staff-email-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Password <span className="text-red-500">*</span></Label>
                <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Login password" required minLength={6} data-testid="sub-staff-password-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Mobile (optional)</Label>
                <Input value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))}
                  placeholder="Mobile number" data-testid="sub-staff-mobile-input" />
              </div>
              <Button type="submit" disabled={submitting}
                className="w-full bg-violet-600 hover:bg-violet-700" data-testid="sub-staff-create-submit">
                {submitting ? 'Creating...' : 'Create Sub-Staff'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team List */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5" data-testid="sub-staff-list">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          Sub-Staff ({team.length})
        </h2>

        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : team.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <Users size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Abhi koi sub-staff nahi hai.</p>
            <p className="text-xs text-gray-400 mt-1">Upar "Add Sub-Staff" button se naya add karo.</p>
          </div>
        ) : (
          <>
            {/* Mobile view */}
            <div className="sm:hidden space-y-3">
              {team.map(ss => (
                <div key={ss.id} className="border border-gray-100 rounded-lg p-3 space-y-1" data-testid={`sub-staff-card-${ss.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium text-gray-900">{ss.name}</p>
                      <p className="text-xs text-gray-500">{ss.email}</p>
                      {ss.mobile && <p className="text-xs text-gray-500">{ss.mobile}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openActivityDialog(ss)}
                      className="text-violet-600 hover:bg-violet-50" data-testid={`activity-sub-staff-${ss.id}`}>
                      <ClockCounterClockwise size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openResetDialog(ss)}
                      className="text-blue-600 hover:bg-blue-50" data-testid={`reset-sub-staff-${ss.id}`}>
                      <Key size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(ss.id, ss.name)}
                      className="text-red-600 hover:bg-red-50" data-testid={`delete-sub-staff-${ss.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-400">Created: {formatDate(ss.created_at)}</p>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.map(ss => (
                    <TableRow key={ss.id} data-testid={`sub-staff-row-${ss.id}`}>
                      <TableCell className="font-medium">{ss.name}</TableCell>
                      <TableCell className="text-sm text-gray-600">{ss.email}</TableCell>
                      <TableCell className="text-sm text-gray-600">{ss.mobile || '-'}</TableCell>
                      <TableCell className="text-xs text-gray-500">{formatDate(ss.created_at)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => openActivityDialog(ss)}
                          className="text-violet-600 hover:bg-violet-50" data-testid={`activity-sub-staff-row-${ss.id}`} title="View activity">
                          <ClockCounterClockwise size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openResetDialog(ss)}
                          className="text-blue-600 hover:bg-blue-50" data-testid={`reset-sub-staff-row-${ss.id}`} title="Reset password">
                          <Key size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(ss.id, ss.name)}
                          className="text-red-600 hover:bg-red-50" data-testid={`delete-sub-staff-row-${ss.id}`} title="Delete">
                          <Trash size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(o) => { if (!o) setResetDialog({ open: false, sub: null }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key size={20} className="text-blue-600" /> Reset Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-2" data-testid="reset-password-form">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-900">{resetDialog.sub?.name}</p>
              <p className="text-xs text-blue-700">{resetDialog.sub?.email}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">New Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                placeholder="Minimum 6 characters" required minLength={6} data-testid="new-password-input" />
              <p className="text-xs text-gray-500 mt-1">Ye password sub-staff ko batana — old password kaam nahi karega.</p>
            </div>
            <Button type="submit" disabled={resetting || newPw.length < 6}
              className="w-full bg-blue-600 hover:bg-blue-700" data-testid="reset-password-submit">
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      {/* Activity Log Dialog */}
      <Dialog open={activityDialog.open} onOpenChange={(o) => { if (!o) setActivityDialog({ open: false, sub: null }); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={20} className="text-violet-600" /> Activity Log — {activityDialog.sub?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2" data-testid="activity-log-content">
            {activityLoading ? (
              <p className="text-sm text-gray-500 py-4 text-center">Loading activity...</p>
            ) : activityList.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <ClockCounterClockwise size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Abhi tak koi activity record nahi hai.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {activityList.map(a => (
                  <div key={a.id} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50" data-testid={`activity-item-${a.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          <ActivityBadge action={a.action} />
                          <span className="ml-2">{formatActionLabel(a.action)}</span>
                        </p>
                        {a.details && Object.keys(a.details).length > 0 && (
                          <div className="mt-1 text-xs text-gray-600 space-y-0.5">
                            {Object.entries(a.details).map(([k, v]) => (
                              <div key={k}><span className="font-medium">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 whitespace-nowrap">{formatDate(a.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ACTION_LABELS = {
  'login': 'Logged in',
  'booking.create': 'Created booking',
  'booking_file.process': 'Processed booking file',
  'hpcl_matcher.process': 'Generated HPCL report',
};
const formatActionLabel = (action) => ACTION_LABELS[action] || action;

const ActivityBadge = ({ action }) => {
  const colorMap = {
    'login': 'bg-green-100 text-green-700',
    'booking.create': 'bg-blue-100 text-blue-700',
    'booking_file.process': 'bg-indigo-100 text-indigo-700',
    'hpcl_matcher.process': 'bg-violet-100 text-violet-700',
  };
  const cls = colorMap[action] || 'bg-gray-100 text-gray-700';
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded ${cls}`}>{action}</span>;
};
